import { AsyncAction, AsyncThunkAction } from 'actions/actions.types'
import { FetchDatingProfileAction } from 'actions/profile/fetchDatingProfileAction'
import { profileDataCompletedAction } from 'actions/profile/profileDataAction'
import { toggleProfileUpdateAction } from 'actions/profile/toggleProfileUpdateAction'
import {
  CascadeProfileFieldType,
  datingProfileOptionsApi,
  getNextFieldApi,
} from 'api/datingProfileApi'
import { definitions } from 'api/generated/lexical_options'
import { changeDatingProfileApi } from 'api/profile/changeDatingProfileApi'
import {
  fieldPathExcludedList,
  fieldPathList,
  fieldWithoutSkipButton,
  fieldWithSingleSelect,
} from 'components/page/DatingProfile/DatingProfile.constants'
import { fieldNamePath } from 'components/page/DatingProfile/DatingProfile.paths'
import { datingProfilePath } from 'components/paths'
import { mergeAllUrls } from 'functions/mergeAllUrls'
import { goBack, replace } from 'functions/router'

export const POST_DATING_PROFILE_FIELD = 'POST_DATING_PROFILE_FIELD' as const
export const GET_DATING_PROFILE_OPTIONS = 'GET_DATING_PROFILE_OPTIONS'
export const UPDATE_IGNORE_FIELDS = 'UPDATE_IGNORE_FIELDS'
export const REMOVE_IGNORE_FIELDS = 'REMOVE_IGNORE_FIELDS'
export const UPDATE_DEFAULT_PROFILE_STATE =
  'UPDATE_DEFAULT_PROFILE_STATE' as const
export const FETCH_NEXT_FIELD_IN_CASCADE = 'FETCH_NEXT_FIELD_IN_CASCADE'
export const REMOVE_ALL_CHOSEN_FIELD_VALUE = 'REMOVE_ALL_CHOSEN_FIELD_VALUE'
export const CHANGE_PROFILE_ITEM = 'CHANGE_PROFILE_ITEM'
export const NEXT_FIELD_IN_CASCADE = 'NEXT_FIELD_IN_CASCADE'
export const SAVE_CURRENT_FIELD = 'SAVE_CURRENT_FIELD'

interface FetchDatingProfileOptionsAction
  extends AsyncAction<{ dictionaries: definitions['LexicalDictionary'][] }> {
  type: typeof GET_DATING_PROFILE_OPTIONS
}

interface UpdateIgnoredFieldsAction {
  type: typeof UPDATE_IGNORE_FIELDS
  value: string
}

interface removeIgnoredFieldAction {
  type: typeof REMOVE_IGNORE_FIELDS
}

interface FetchNextCascadeFieldAction extends AsyncAction {
  type: typeof FETCH_NEXT_FIELD_IN_CASCADE
  baseUrl: string
  isFromListFields: boolean
}

interface removeAllChosenFieldValueAction {
  type: typeof REMOVE_ALL_CHOSEN_FIELD_VALUE
}

interface IsChangeProfileItemAction {
  type: typeof CHANGE_PROFILE_ITEM
  isChangeProfileItem: boolean
}

interface nextFieldInCascadeAction {
  type: typeof NEXT_FIELD_IN_CASCADE
  value: boolean
}

interface saveCurrentFieldAction {
  type: typeof SAVE_CURRENT_FIELD
  value: string
}

export const changeDatingProfileAction =
  (oldValue: string, newValue: string, fieldName: string): AsyncThunkAction =>
  (dispatch) => {
    const value = returnCurrentValue(oldValue, newValue)

    dispatch(changeDatingProfileReduxAction(fieldName, value))

    if (fieldWithSingleSelect.includes(fieldName)) {
      dispatch(postDatingProfileFieldAction(fieldName, value, false))
    }
  }

export const postDatingProfileFieldAction =
  (
    fieldName: string,
    fieldValue: string,
    shouldReturnPreviousPage: boolean
  ): AsyncThunkAction =>
  async (dispatch) => {
    const { result: json } = (await dispatch(
      postDatingProfileFieldPlainAction(fieldName, fieldValue)
    )) as unknown as {
      result: { ok: boolean }
    }

    dispatch(updateDefaultProfileStateAction(fieldName, fieldValue))

    // Теперь на поле не из каскада можно так же попасть на прямую.
    // Надо подумать как вернуть пользователя минуя список, но при этом сохранить возможность возвращаться в список
    if (json.ok) {
      if (shouldReturnPreviousPage) {
        dispatch(toggleProfileUpdateAction(true))
        dispatch(goBack())
      } else {
        dispatch(ignoreFieldsAction(fieldName))
        dispatch(isChangeProfileItemAction(true))
        dispatch(nextFieldInCascadeAction(true))
      }
    }
  }

export const postDatingProfileFieldPlainAction = (
  fieldName: string,
  fieldValue: string
) => ({
  type: POST_DATING_PROFILE_FIELD,
  promise: () => changeDatingProfileApi(fieldName, fieldValue),
})

export const fetchNextCascadeFieldAction =
  (): AsyncThunkAction => (dispatch, getState) => {
    const { ignoredFields, isChangeProfileItem } =
      getState().datingProfileReducer
    const { baseUrl } = getState().systemReducer
    dispatch({
      type: FETCH_NEXT_FIELD_IN_CASCADE,
      promise: async () => {
        const json = await getNextFieldApi(
          ignoredFields.join(',') as unknown as CascadeProfileFieldType
        )

        if (json.result) {
          const { name: field } = json.result
          const fieldPath = fieldPathList[field] || fieldPathExcludedList[field]

          dispatch(nextFieldInCascadeAction(false))

          if (field) {
            const url = mergeAllUrls(
              baseUrl,
              datingProfilePath,
              fieldNamePath,
              fieldPath
            )
            dispatch(replace(url))
          } else {
            /**
             * Приходит ошибка profile_fields_ended_for_cascade_change
             * это значит что больше полей в каскаде нету, и либо закрыть, либо вернутся на список полей.
             */
            if (
              !json.ok &&
              /** @ts-expect-error Неполнота типа result */
              json.result?.code === 'profile_fields_ended_for_cascade_change'
            ) {
              dispatch(goBack())
              if (isChangeProfileItem) {
                dispatch(profileDataCompletedAction())
              }
            }
          }
        }

        return json
      },
    })
  }

export const removeIgnoredFieldAction = () => {
  return {
    type: REMOVE_IGNORE_FIELDS,
  }
}

export const removeAllChosenFieldValueAction = () => {
  return {
    type: REMOVE_ALL_CHOSEN_FIELD_VALUE,
  }
}

export const nextFieldInCascadeAction = (value: boolean) => {
  return {
    type: NEXT_FIELD_IN_CASCADE,
    value,
  }
}

export const updateIgnoredFieldsAction = (value: string) => ({
  type: UPDATE_IGNORE_FIELDS,
  value,
})

export const updateDefaultProfileStateAction = (
  name: string,
  value: string
) => ({
  type: UPDATE_DEFAULT_PROFILE_STATE,
  name,
  value,
})

export const isChangeProfileItemAction = (isChangeProfileItem: boolean) => ({
  type: CHANGE_PROFILE_ITEM,
  isChangeProfileItem,
})

export const fetchDatingProfileOptionsAction =
  (field: string, ...rest): AsyncThunkAction =>
  (dispatch) => {
    dispatch({
      type: GET_DATING_PROFILE_OPTIONS,
      promise: () => datingProfileOptionsApi(field, ...rest),
    })
  }

export const ignoreFieldsAction =
  (value: string): AsyncThunkAction =>
  (dispatch) => {
    const fieldNotInTheCascade = fieldWithoutSkipButton.includes(value)

    if (!fieldNotInTheCascade) {
      dispatch(updateIgnoredFieldsAction(value))
    }
  }

export const CHANGE_DATING_PROFILE_LOOK_FOR_AGE_RANGE =
  'CHANGE_DATING_PROFILE_LOOK_FOR_AGE_RANGE'

interface datingProfileChangeLookForAgeRangeAction {
  type: typeof CHANGE_DATING_PROFILE_LOOK_FOR_AGE_RANGE
  value: number
  name: string
}

export const datingProfileChangeLookForAgeRangeAction = (
  name: string,
  value: number
) => ({
  type: CHANGE_DATING_PROFILE_LOOK_FOR_AGE_RANGE,
  name,
  value,
})

export const CHANGE_DATING_PROFILE_CHARACTER = 'CHANGE_DATING_PROFILE_CHARACTER'

interface datingProfileChangeCharacterAction {
  type: typeof CHANGE_DATING_PROFILE_CHARACTER
  value: number
  name: string
}

export const datingProfileChangeCharacterAction = (
  name: string,
  value: number
) => ({
  type: CHANGE_DATING_PROFILE_CHARACTER,
  name,
  value,
})

export const CHANGE_DATING_PROFILE_FIELD = 'CHANGE_DATING_PROFILE_FIELD'

interface ChangeDatingProfileReduxAction {
  type: typeof CHANGE_DATING_PROFILE_FIELD
  name: string
  value: string
}

export const changeDatingProfileReduxAction = (
  name: string,
  value: string
) => ({
  type: CHANGE_DATING_PROFILE_FIELD,
  name,
  value,
})

/**
 * Пустая строка переданная в имя поле - это сброс
 * @param fieldName
 */
export const updateCurrentFieldAction = (fieldName?: string | undefined) => ({
  type: SAVE_CURRENT_FIELD,
  value: fieldName,
})

const returnCurrentValue = (oldValue, newValue) => {
  const isArray = Array.isArray(oldValue)
  const isExist = isArray ? oldValue.includes(newValue) : oldValue === newValue

  if (isArray) {
    return isExist && oldValue.length
      ? oldValue.filter((valueItem) => valueItem !== newValue)
      : oldValue.concat(newValue)
  } else {
    return isExist ? null : newValue
  }
}

export const replaceFieldDatingProfileAction =
  (fieldName: string, redirectFieldName: string): AsyncThunkAction =>
  (dispatch, getState) => {
    const baseUrl = getState().systemReducer.baseUrl
    const url = mergeAllUrls(
      baseUrl,
      datingProfilePath,
      fieldNamePath,
      redirectFieldName
    )

    dispatch(updateCurrentFieldAction(fieldName))
    dispatch(replace(url))
  }

export type DatingProfileTypes =
  | FetchDatingProfileAction
  | datingProfileChangeLookForAgeRangeAction
  | datingProfileChangeCharacterAction
  | ChangeDatingProfileReduxAction
  | ReturnType<typeof postDatingProfileFieldPlainAction>
  | FetchDatingProfileOptionsAction
  | UpdateIgnoredFieldsAction
  | removeIgnoredFieldAction
  | ReturnType<typeof updateDefaultProfileStateAction>
  | FetchNextCascadeFieldAction
  | removeAllChosenFieldValueAction
  | IsChangeProfileItemAction
  | nextFieldInCascadeAction
  | saveCurrentFieldAction
